import React from 'react';
import { clsx } from 'clsx';
import {
  ChevronRightIcon,
  ChevronDownIcon,
  CornerDownRightIcon,
  ExternalLinkIcon,
  ShuffleIcon,
  FocusIcon
} from 'lucide-react';
import { FlowLogEvent } from '../../../types/flow-log';
import { InteractionLogEntry } from '../../../types/interaction-log-data';

import { Badge, Button, CopyButton } from '@@/ui/primitives';
import { Link } from 'react-router-dom';
import { getColorForString } from '../../flow-color';
import { LambdaMetricsSheet } from '../../sheets/lambda-metrics-sheet';

export interface LogItemProps {
  instanceAlias: string;
  accountId: string;
  timestamp: string;
  title: string;
  subtitle?: string;
  content?: string;
  parameters?: Record<string, unknown>;
  result?: string;
  identifier?: string;
  icon?: React.ReactNode;
  indentLevel?: number;
  grouped?: boolean;
  index: number;
  isExpanded?: boolean;
  raw: FlowLogEvent | InteractionLogEntry;
  onToggle?: () => void;
  onTitleClick?: (event: FlowLogEvent | InteractionLogEntry) => void;
  customButtons?: React.ReactNode[];
}

function getLambdaLogGroupUrl(raw: FlowLogEvent) {
  if (raw.ContactFlowModuleType !== 'InvokeExternalResource') return null;

  const arn = raw.Parameters?.FunctionArn;
  if (typeof arn !== 'string') return null;

  const functionName = arn.split(':').pop();
  const region = arn.split(':')[3];

  return `https://console.aws.amazon.com/cloudwatch/home?region=${region}#logsV2:log-groups/log-group/$252Faws$252Flambda$252F${functionName}`;
}

function getLambdaUrl(raw: FlowLogEvent) {
  if (raw.ContactFlowModuleType !== 'InvokeExternalResource') return null;

  const arn = raw.Parameters?.FunctionArn;
  if (typeof arn !== 'string') return null;

  const functionName = arn.split(':').pop();
  const region = arn.split(':')[3];

  return `https://console.aws.amazon.com/lambda/home?region=${region}#/functions/${functionName}`;
}

const getContactFlowUrl = (instanceUrl: string, raw: FlowLogEvent | InteractionLogEntry) => {
  if ('ContactFlowId' in raw) {
    return `${instanceUrl}/contact-flows/edit?id=${raw.ContactFlowId}&actionId=${raw.Identifier}`;
  }

  if ('LogData' in raw && raw.LogData.ResourceArn) {
    const arnParts = raw.LogData.ResourceArn.split('/contact-flow/')[1];
    if (arnParts) {
      const contactFlowId = arnParts.split('/')[0];
      return `${instanceUrl}/contact-flows/edit?id=${contactFlowId}`;
    }
  }

  return null;
};

export const LogItem = (props: LogItemProps) => {
  const isError = ['error', 'false'].includes(props.result?.toLowerCase() ?? '');
  const isLambda = props.title === 'InvokeExternalResource';

  const instanceUrl = `https://${props.instanceAlias}.my.connect.aws`;
  const contactFlowUrl = getContactFlowUrl(instanceUrl, props.raw);

  const indentLevel = props.indentLevel ?? 0;

  // Get operation type from parameters for color-coded borders
  const operationType = props.parameters?._operationType as string | undefined;
  const hasOperationType = (_type: string | undefined): boolean => {
    if (indentLevel <= 0) return false;
    return operationType === _type;
  };

  return (
    <div
      key={props.index}
      className={clsx(
        'bg-white',
        'relative',
        indentLevel === 0 ? 'not-first:border-t -mt-px border-neutral-200' : 'ml-3 border-b border-neutral-200'
      )}
    >
      <div
        className={clsx(
          'px-2 text-xs',
          indentLevel > 0 && 'border-l-2 pl-0',
          hasOperationType('agent') && 'border-l-purple-400',
          hasOperationType('inference') && 'border-l-blue-200',
          hasOperationType('knowledge') && 'border-l-green-600',
          hasOperationType('tool') && 'border-l-orange-400',
          hasOperationType('default') && 'border-l-neutral-400'
        )}
      >
        <div
          className={clsx('group', 'flex items-center gap-2 relative h-8 cursor-pointer hover:bg-neutral-50')}
          onClick={props.onToggle}
        >
          <div
            className={clsx(
              'flex items-center justify-center cursor-pointer w-4',
              props.isExpanded ? 'text-neutral-500' : 'text-neutral-400'
            )}
          >
            {props.isExpanded ? (
              <ChevronDownIcon className='size-3 text-neutral-500' />
            ) : (
              <ChevronRightIcon className='size-3 text-neutral-500' />
            )}
          </div>
          <div className='text-neutral-500 tabular-nums w-22'>{props.timestamp}</div>
          {(props.raw as FlowLogEvent).ContactFlowName && (
            <>
              <div
                style={{
                  color: getColorForString((props.raw as FlowLogEvent).ContactFlowName).textColor
                }}
                className={clsx(
                  'font-medium max-w-36 min-w-36 truncate',
                  'transition-opacity duration-100',
                  props.isExpanded && 'opacity-50 group-hover:opacity-100'
                )}
              >
                {(props.raw as FlowLogEvent).ContactFlowName}
              </div>
            </>
          )}
          <div
            className={clsx(
              'flex items-center w-4',
              'transition-opacity duration-100',
              props.isExpanded && 'opacity-50 group-hover:opacity-100 pointer-events-none select-none'
            )}
          >
            {props.icon}
          </div>
          <div
            className={clsx(
              'font-medium max-w-46 min-w-46 truncate cursor-pointer flex items-center',
              'transition-opacity duration-100',
              props.isExpanded && 'opacity-50 group-hover:opacity-100'
            )}
          >
            <span className='truncate'>{props.title}</span>
            <div className='flex-1' />
            {props.onTitleClick && (
              <Button
                variant='ghost'
                size='xs'
                className='p-0 text-xs! gap-2 rounded-lg'
                onClick={(e) => {
                  e.stopPropagation();
                  props.onTitleClick!(props.raw);
                }}
              >
                <FocusIcon className='size-3 text-neutral-500' />
              </Button>
            )}
            {contactFlowUrl && (
              <Link to={contactFlowUrl} target='_blank'>
                <Button variant='ghost' size='xs' className='p-0 text-xs! gap-2 rounded-lg'>
                  <ExternalLinkIcon className='size-3 text-neutral-500' />
                </Button>
              </Link>
            )}
          </div>

          {props.subtitle && (
            <div
              className={clsx(
                'flex-1 flex items-center gap-2',
                'transition-opacity duration-100',
                props.isExpanded && 'opacity-50 group-hover:opacity-100 select-none'
              )}
            >
              {props.grouped && (
                <>
                  <CornerDownRightIcon className='inline size-3 min-w-3 text-neutral-500' />
                </>
              )}
              <span className='font-medium text-neutral-700 line-clamp-1'>{props.subtitle}</span>
              {props.customButtons?.map((button, index) => (
                <div key={index} onClick={(e) => e.stopPropagation()}>
                  {button}
                </div>
              ))}
              {isLambda &&
                typeof props.parameters?.FunctionArn === 'string' &&
                props.accountId !== props.parameters.FunctionArn.split(':')[4] && (
                  <Badge className='rounded-full! text-orange-950! border bg-transparent border-orange-300! px-2! text-xs! py-0.5'>
                    <ShuffleIcon className='size-3.5' />
                    Cross-account
                  </Badge>
                )}
              {isLambda && (
                <Link to={getLambdaLogGroupUrl(props.raw as FlowLogEvent) ?? ''} target='_blank'>
                  <Button variant='outline' className='text-xs! h-min px-2! rounded-full font-medium! py-0.5'>
                    {/* <LogsIcon className='size-3.5' /> */}
                    <ExternalLinkIcon className='size-3.5' />
                    Logs
                  </Button>
                </Link>
              )}
              {isLambda && (
                <Link to={getLambdaUrl(props.raw as FlowLogEvent) ?? ''} target='_blank'>
                  <Button variant='outline' className='text-xs! h-min px-2! rounded-full font-medium! py-0.5 gap-1'>
                    <ExternalLinkIcon className='size-3.5' />
                    Lambda
                  </Button>
                </Link>
              )}
              {isLambda && (
                <div onClick={(e) => e.stopPropagation()}>
                  <LambdaMetricsSheet raw={props.raw as FlowLogEvent} />
                </div>
              )}
            </div>
          )}
          {props.result && (
            <div
              className={clsx(
                'font-medium',
                !isError ? 'text-green-950' : 'text-red-950',
                'transition-opacity duration-100',
                props.isExpanded && 'opacity-50 group-hover:opacity-100 pointer-events-none select-none'
              )}
            >
              {props.result}
            </div>
          )}
          {props.identifier && (
            <div
              className={clsx(
                'flex items-center gap-2 ml-auto px-2',
                'transition-opacity duration-100',
                'relative',
                props.isExpanded && 'opacity-50 group-hover:opacity-100 pointer-events-none select-none'
              )}
            >
              <span className='font-mono tabular-nums text-neutral-500 text-xs truncate'>{props.identifier}</span>
            </div>
          )}
        </div>
        {props.isExpanded && (
          <div className='py-2 flex flex-col gap-1 pl-30 pr-40 z-50!'>
            {props.content && (
              <div className='p-2 bg-neutral-100 rounded text-xs'>
                <div className='font-medium mb-1'>Content</div>
                <div className='whitespace-pre-wrap'>{props.content}</div>
              </div>
            )}
            {props.raw && (
              <pre className='relative bg-neutral-100 whitespace-pre-wrap rounded-md p-2 px-3 text-xs!'>
                <div className='absolute top-2 right-2'>
                  <CopyButton content={JSON.stringify(props.raw, null, 2)} />
                </div>

                <code>{JSON.stringify(props.raw, null, 2)}</code>
              </pre>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
